import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, GraduationCap, Loader2 } from 'lucide-react';
import { OnlineAdmissionPublicApi, type PublicAdmissionSchoolContact } from '@/services/online-admission-public.api';
import { Field, Input, Select } from '@/components/ui/Input';

const GENDERS = ['male', 'female'] as const;
const SOURCE_OPTIONS = ['Instagram', 'Facebook', 'YouTube', 'Website', 'Notice', 'Through Staff', 'Already a Parent'] as const;

/**
 * Online Admission — public application form (no auth, matches legacy's
 * publicCreate/publicStore). ONE shared URL for every school — the
 * applicant picks their "Center" from a dropdown, matching legacy exactly
 * (School::whereHas('onlineAdmissionClasses')->where('status', 1)), not a
 * per-school URL. Class list is dob-filtered server-side once a dob is
 * entered (see OnlineAdmissionPublicApi's doc comment) — this is a genuine
 * server-computed filter, not just a display convenience, and the backend
 * independently re-validates the same range on submit regardless of what
 * this form sent (the security fix legacy never had).
 */
export function OnlineAdmissionApplyPage() {
  const [name, setName] = useState('');
  const [gender, setGender] = useState<(typeof GENDERS)[number] | ''>('');
  const [dob, setDob] = useState('');
  const [mobile, setMobile] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [fatherName, setFatherName] = useState('');
  const [schoolSlug, setSchoolSlug] = useState('');
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [place, setPlace] = useState('');
  const [sourceOfInfo, setSourceOfInfo] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState<string | null>(null);
  const [submittedSchool, setSubmittedSchool] = useState<PublicAdmissionSchoolContact | null>(null);

  const { data: centers = [], isFetching: loadingCenters } = useQuery({
    queryKey: ['public-admission-centers'],
    queryFn: () => OnlineAdmissionPublicApi.listCenters(),
  });

  const { data: classes = [], isFetching: loadingClasses } = useQuery({
    queryKey: ['public-admission-classes', schoolSlug, dob],
    queryFn: () => OnlineAdmissionPublicApi.listClasses(schoolSlug, dob || undefined),
    enabled: !!schoolSlug,
  });

  const selectedClass = classes.find((c) => c.id === classId);

  useEffect(() => {
    // The dob-filtered class list changed under the selected class — clear it rather than silently keep an ineligible selection.
    if (classId && !classes.some((c) => c.id === classId)) {
      setClassId('');
      setSectionId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classes]);

  const { data: sections = [] } = useQuery({
    queryKey: ['public-admission-sections', schoolSlug, classId],
    queryFn: () => OnlineAdmissionPublicApi.listSections(schoolSlug, classId),
    enabled: !!classId,
  });

  useEffect(() => {
    // Matches legacy: the section picker is hidden — the first section for
    // the chosen class is auto-selected and submitted silently, satisfying
    // the backend's required class_section_id without asking the applicant
    // to pick one (a real school-side concept, not something a public
    // applicant would meaningfully choose between).
    setSectionId(sections[0]?.id ?? '');
  }, [sections]);

  const submit = async () => {
    setError(null);
    if (!name.trim() || !gender || !dob || !mobile.trim() || !fatherName.trim() || !schoolSlug || !classId || !sectionId || !place.trim()) {
      setError('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await OnlineAdmissionPublicApi.apply(schoolSlug, {
        name: name.trim(),
        gender,
        dob,
        mobile: mobile.trim(),
        whatsapp: whatsapp.trim() || undefined,
        fatherName: fatherName.trim(),
        classId,
        sectionId,
        place: place.trim(),
        sourceOfInfo: sourceOfInfo.trim() || undefined,
      });
      setSubmittedId(res.id);
      setSubmittedSchool(res.school);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message ?? 'Could not submit your application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submittedId) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
        <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <CheckCircle2 className="mx-auto mb-3 h-12 w-12 text-emerald-500" />
          <h1 className="text-lg font-semibold text-slate-900">Application Submitted Successfully!</h1>
          <p className="mt-2 text-sm text-slate-500">The school will review your application and get in touch with you.</p>

          {submittedSchool && (
            <div className="mt-6 rounded-lg border border-emerald-100 bg-emerald-50 p-4 text-left">
              <h2 className="text-sm font-semibold text-emerald-800">School Contact Details</h2>
              <dl className="mt-2 space-y-1 text-sm text-emerald-900">
                <div className="flex justify-between gap-2">
                  <dt className="text-emerald-700">Center:</dt>
                  <dd className="font-medium">{submittedSchool.name}</dd>
                </div>
                {submittedSchool.contact && (
                  <div className="flex justify-between gap-2">
                    <dt className="text-emerald-700">Admin Contact:</dt>
                    <dd className="font-medium">{submittedSchool.contact}</dd>
                  </div>
                )}
              </dl>
              <p className="mt-3 text-xs italic text-emerald-700">Please contact the school office for any further queries.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10">
      <div className="mx-auto max-w-xl">
        <div className="mb-6 flex items-center gap-2 text-brand-700">
          <GraduationCap className="h-6 w-6" />
          <h1 className="text-xl font-bold">Online Admission Application</h1>
        </div>

        <div className="space-y-5 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

          <Field label="Student Name" required>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Gender" required>
              <Select value={gender} onChange={(e) => setGender(e.target.value as any)}>
                <option value="">Select</option>
                {GENDERS.map((g) => (
                  <option key={g} value={g}>
                    {g[0].toUpperCase() + g.slice(1)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Date of Birth" required>
              <Input type="date" value={dob} onChange={(e) => setDob(e.target.value)} max={new Date().toISOString().slice(0, 10)} />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Mobile No" required>
              <Input value={mobile} onChange={(e) => setMobile(e.target.value)} />
            </Field>
            <Field label="WhatsApp No">
              <Input value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} />
            </Field>
          </div>

          <Field label="Father's Name" required>
            <Input value={fatherName} onChange={(e) => setFatherName(e.target.value)} />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Center" required>
              <Select
                value={schoolSlug}
                onChange={(e) => {
                  setSchoolSlug(e.target.value);
                  setClassId('');
                  setSectionId('');
                }}
                disabled={loadingCenters}
              >
                <option value="">{loadingCenters ? 'Loading…' : 'Select center'}</option>
                {centers.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Class Applying For" required>
              <Select
                value={classId}
                onChange={(e) => {
                  setClassId(e.target.value);
                  setSectionId('');
                }}
                disabled={!schoolSlug || loadingClasses}
              >
                <option value="">
                  {!schoolSlug ? 'Select a center first' : loadingClasses ? 'Loading…' : dob ? (classes.length ? 'Select' : 'No classes available for this DOB') : 'Select'}
                </option>
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
              {schoolSlug && !dob && <p className="mt-1 text-xs text-slate-400">Enter date of birth to see eligible classes.</p>}
            </Field>
          </div>

          <Field label="Place" required>
            <Input value={place} onChange={(e) => setPlace(e.target.value)} />
          </Field>
          {selectedClass?.message && <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700">{selectedClass.message}</p>}

          <Field label="How did you hear about us?">
            <Select value={sourceOfInfo} onChange={(e) => setSourceOfInfo(e.target.value)}>
              <option value="">Select Source</option>
              {SOURCE_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </Field>

          <button className="btn-primary w-full justify-center" onClick={submit} disabled={submitting}>
            {submitting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            Submit Application
          </button>
        </div>
      </div>
    </div>
  );
}
